<template>
  <CompaniesAnalysisLayout
    :not-available-type="modifiedNotAvailableType"
    :company-uuid="$route.params.company_uuid"
    :is-more-than-editor="showMoreThanEditorAuthority"
  >
    <!-- ヘッダー -->
    <template #header>
      <CompaniesAnalysisHeader
        :current-date="date"
        :period="period"
        :visible-export-csv="canExportAuthority('analysis/reviews') && providedFeaturesCsvExport"
        :disabled-export-csv="aggsLoading"
        @change-date="changeDate"
        @change-period="changePeriod"
        @export-csv="setExportCsv"
      >
        <template #select>
          <ul class="c-links">
            <li v-for="type in dataTypes" :key="type.id" class="c-links__item">
              <strong
                :class="{ 'is-current': dataType.id === type.id }"
                @click="changeDataType(type)"
              >
                {{ type.name }}
              </strong>
            </li>
          </ul>
        </template>
      </CompaniesAnalysisHeader>
    </template>

    <!-- グラフ -->
    <template #graph>
      <div class="p-lineChart">
        <div class="p-lineChart__legends">
          <div v-for="item in colorSelectedList" :key="item.id" class="p-lineChart__legends__items">
            <div class="p-lineChart__legends__items--color" :style="`background-color:${item.color};`" />
            <div class="p-lineChart__legends__items--text">{{ item.value }}</div>
          </div>
        </div>
        <Loading v-if="loading || axesLoading" />
        <ChartLine
          v-else-if="graphType === 'line'"
          class="p-lineChart__container"
          :chart-data="chartData"
          :options="chartOptions"
          :height="360"
        />
        <ChartBar
          v-else-if="graphType === 'bar'"
          class="p-lineChart__container"
          :chart-data="chartData"
          :options="chartOptions"
          :height="360"
        />
      </div>
    </template>

    <!-- 分析軸選択 -->
    <template #terms>
      <CompaniesAnalysisCrossSearch
        v-if="!axesLoading"
        :disabled="loading"
        :cross-search-field="crossSearchField"
        :current-page-type="pageType"
        :page-types="axes"
        @set-page-type="changePageType"
        @set-cross-search-field="changeCrossSearchField"
      />
    </template>

    <!-- 競合切り替え -->
    <template #competitor>
      <div v-if="providedFeaturesCompetitor" style="display: flex;">
        {{ $t('pages.companies.analysis.reviews.competitorAnalysis') }}：
        <div class="c-radio__item">
          <input
            :id="'competitor_radio_btn'"
            :checked="competitorMode"
            class="c-radio__input"
            type="checkbox"
            :disabled="loading"
            @change="changeCompetitorMode($event.target.checked)"
          >
          <label class="c-radio__label" :for="'competitor_radio_btn'">{{ $t('pages.companies.analysis.reviews.displayCompetitorData') }}</label>
        </div>
      </div>
    </template>

    <!-- 現在の表示リスト / 表示リストのリセット -->
    <template #reset>
      <ul v-show="individualAnalysisName" class="c-links">
        <li class="c-links__item">
          <strong @click="changeIndividualAnalysisField(null)">{{ $t('pages.companies.analysis.individual.reset') }}</strong> | {{ $t('pages.companies.analysis.individual.listTitle', { name: individualAnalysisName }) }}
        </li>
      </ul>
    </template>

    <!-- テーブル -->
    <template #table>
      <CompaniesAnalysisTable
        :loading="aggsLoading || axesLoading"
        :items="aggs"
        :static-heads="dataTypes"
        :cross-search-fields="crossSearchFields"
        :search-items="displayGraphs"
        :competitor-mode="competitorMode"
        :show-review-link="true"
        @change-display-graphs="changeDisplayGraphs"
        @change-individual-analysis-field="changeIndividualAnalysisField"
      />
    </template>
  </CompaniesAnalysisLayout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout'
import CompaniesAnalysisHeader from '../../../components/CompaniesAnalysis/Header.vue'
import CompaniesAnalysisCrossSearch from '../../../components/CompaniesAnalysis/CrossSearch.vue'
import CompaniesAnalysisTable from '../../../components/CompaniesAnalysis/Table.vue'
import Loading from '../../../components/Loading'
import ChartLine from '../../../components/ChartLine'
import ChartBar from '../../../components/ChartBar'
import companiesNotAvailableTypeMixin from '../../../mixins/companiesNotAvailableTypeMixin'
import { colorNumbers, colors, stackedBarColorNumbers } from '../../../modules/analysis'
import { summaryCustomTooltip } from '../../../modules/customTooltip.ts'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'CompaniesAnalysisReviews',
  components: {
    CompaniesAnalysisLayout,
    CompaniesAnalysisHeader,
    CompaniesAnalysisCrossSearch,
    CompaniesAnalysisTable,
    Loading,
    ChartLine,
    ChartBar,
  },
  mixins: [companiesNotAvailableTypeMixin],
  beforeRouteUpdate(to, from, next) {
    from.query.cross = this.crossSearchField?.fields
    this.initParams(to.query.mode, to.query.type, to.query.fields)
    to.query.cross = this.crossSearchField?.fields
    if (!this.modifiedNotAvailableType) {
      this.fetchData(this.evalNeedFetchAggsData(to.query, from.query))
    }
    next()
  },
  data() {
    return {
      title: '',
      colorSelectedList: []
    }
  },
  computed: {
    ...mapState({
      plots: state => state.companiesAnalysisReviews.plots,
      axesLoading: state => state.companiesAnalysisAxes.loading,
      // NOTE: 使用に制限のある軸があるため、computedに定義したaxesの方を利用する
      allAxes: state => state.companiesAnalysisAxes.data,
      aggsLoading: state => state.companiesAnalysisReviews.aggsLoading,
      plotsLoading: state => state.companiesAnalysisReviews.plotsLoading,
      dataType: state => state.companiesAnalysisReviews.dataType,
      date: state => state.companiesAnalysisReviews.date,
      displayGraphs: state => state.companiesAnalysisReviews.displayGraphs,
      graphType: state => state.companiesAnalysisReviews.graphType,
      graphKey: state => state.companiesAnalysisReviews.graphKey,
      pageType: state => state.companiesAnalysisReviews.pageType,
      crossSearchField: state => state.companiesAnalysisReviews.crossSearchField,
      competitorMode: state => state.companiesAnalysisReviews.competitorMode,
      period: state => state.filters.period
    }),
    ...mapGetters({
      crossSearchFields: 'companiesAnalysisReviews/crossSearchFields',
      individualAnalysisName: 'companiesAnalysisReviews/individualAnalysisName',
      aggs: 'companiesAnalysisReviews/aggs',
      selectedAggIds: 'companiesAnalysisReviews/selectedAggIds',
      providedFeaturesCompetitor: 'companiesCompany/providedFeaturesCompetitor',
      dataTypes: 'companiesAnalysisReviews/dataTypes',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      inboundOption: 'companiesCompany/inboundOption',
      neverAnalysisReviews: 'companiesUserActivity/neverAnalysisReviews',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
    }),
    /**
     * テーブルまたはグラフデータ取得中かどうか
     * @return { Boolean }
     */
    loading() {
      return this.aggsLoading || this.plotsLoading
    },
    /**
     * グラフまたはテーブルデータ取得 API に渡すパラメータ
     * @return { Object }
     */
    requestParams() {
      return {
        companyUuid: this.$route.params.company_uuid,
        params: {
          posted_at_term: `${ moment(this.period.startDate).format('YYYY/MM/DD') } - ${ moment(this.period.endDate).format('YYYY/MM/DD') }`
        }
      }
    },
    /**
     * グラフライブラリに渡すデータ
     * @return { Object }
     */
    chartData() {
      const datasets = []
      let localCount = 0
      this.colorSelectedList = []
      for (const key in this.plots) {
        if (!this.selectedAggIds.includes(key)) continue
        const plot = this.plots[key]
        if (plot == null) continue
        if (this.dataType.id === 'counts') {
          this.colorSelectedList.push({id:localCount, value: plot.label, color: colors[localCount % 10]})
          const ratingList = Object.values(plot[this.dataType.dataType])
          const unrated = ratingList.map(rating => rating.unrated)
          const rating1 = ratingList.map(rating => rating.rating_1)
          const rating2 = ratingList.map(rating => rating.rating_2)
          const rating3 = ratingList.map(rating => rating.rating_3)
          const rating4 = ratingList.map(rating => rating.rating_4)
          const rating5 = ratingList.map(rating => rating.rating_5)
          const items = [[...unrated], [...rating1], [...rating2], [...rating3], [...rating4], [...rating5]]
          for(let i = 0; i < items.length; i++){
            datasets.push({
              label: [plot.label,`★${i === 0 ? 'なし' : i}`],
              data: items[i],
              backgroundColor: `rgba(${ stackedBarColorNumbers[localCount % 10][i] })`,
              lineTension: 0,
              pointRadius: 4,
              stack: `${key}`
            })
          }
          localCount++
          continue
        }
        const data = Object.values(plot[this.dataType.dataType])
        datasets.push({
          label: plot.label,
          data,
          backgroundColor: Array(data.length).fill(`rgba(${ colorNumbers[[datasets.length % 10]] })`),
          borderColor: this.graphType === 'bar' ? Array(data.length).fill(colors[datasets.length % 10]) : [colors[datasets.length % 10]],
          borderWidth: 2,
          pointBorderColor: Array(data.length).fill(colors[datasets.length % 10]),
          lineTension: 0,
          pointRadius: 4,
        })
      }
      return { labels: this.graphKey, datasets }
    },
    /**
     * グラフライブラリに渡すオプション
     * @return { Object }
     */
    chartOptions() {
      const y = { beginAtZero: true }
      const x = {}
      const legend = {}
      const layout = {}
      if (this.dataType.id === 'scores') {
        y.max = 5
      } else if (this.dataType.id === 'counts'){
        y.stacked = true
        x.stacked = true
        legend.display = false
        layout.padding = { bottom: 30 }
      }
      return {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 4,
        scales: {
          y,
          x,
        },
        layout,
        plugins: {
          legend,
          tooltip: this.toolTip
        }
      }
    },
    toolTip() {
      if (this.dataType.id === 'counts') {
        return {
          mode: 'x',
          enabled: false,
          external: (context) => {
            summaryCustomTooltip(context, '合計件数')
          },
        }
      } else {
        return {
          callbacks: {
            label: function (context) {
              if (context.chart.options.scales.x.stacked) return context.dataset.label[1] + ': ' + context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              return context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            },
            beforeBody: function (tooltipItems) {
              if (!tooltipItems[0].chart.options.scales.x.stacked) return
              return tooltipItems[0].dataset.label[0]
            }
          },
        }
      }
    },
    /**
     * @return { String }
     */
    modifiedNotAvailableType() {
      if ((!this.competitorMode && this.notAvailableType === 'emptyCompetitor') || this.axesLoading) return null
      return this.notAvailableType
    },
    // NOTE: インバウンドオプションが有効の場合のみ、言語別が利用可能
    axes() { return this.inboundOption ? this.allAxes : this.allAxes.filter(v => !['locale'].includes(v.id)) }
  },
  async created() {
    await this.readCompaniesAnalysisAxes()
    this.initParams(this.$route.query.mode, this.$route.query.type, this.$route.query.fields)
    if (this.modifiedNotAvailableType) return
    this.fetchData(true)
  },
  methods: {
    ...mapActions({
      setDataType: 'companiesAnalysisReviews/setDataType',
      setDate: 'companiesAnalysisReviews/setDate',
      setDisplayGraphs: 'companiesAnalysisReviews/setDisplayGraphs',
      setPageType: 'companiesAnalysisReviews/setPageType',
      setCrossSearchField: 'companiesAnalysisReviews/setCrossSearchField',
      setIndividualAnalysisField: 'companiesAnalysisReviews/setIndividualAnalysisField',
      setCompetitorMode: 'companiesAnalysisReviews/setCompetitorMode',
      readAggs: 'companiesAnalysisReviews/readAggs',
      readPlots: 'companiesAnalysisReviews/readPlots',
      exportCsv: 'companiesAnalysisReviews/exportCsv',
      setPeriod: 'filters/setPeriod',
      clearNeverReadAnalysisReviews: 'companiesUserActivity/clearNeverReadAnalysisReviews',
      readCompaniesAnalysisAxes: 'companiesAnalysisAxes/readCompaniesAnalysisAxes'
    }),
    /**
     * 表のデータの再取得が必要かどうかを判定して返す
     * @return Boolean
     **/
    evalNeedFetchAggsData(toQuery, fromQuery) {
      return toQuery.fields !== fromQuery.fields || toQuery.mode !== fromQuery.mode || toQuery.cross !== fromQuery.cross
    },
    initParams(mode, type, fields) {
      // pageTypeが変更されていない===pageType以外が変更された
      if (type === this.pageType.id) this.setIndividualAnalysisField(null)
      this.setCompetitorMode(mode === 'competitor')
      if (this.modifiedNotAvailableType) this.setCrossSearchField(null)
      this.setDataType(this.dataTypes.find(v => v.id === type) || this.dataTypes[0])
      this.setPageType(fields || 'store_id')
    },
    /**
     * APIを叩く
     */
    async fetchData(fetchAggs) {
      if (fetchAggs) {
        await this.readAggs(this.requestParams)
      }
      if (this.aggs.length) {
        await this.readPlots(this.requestParams)
      }
      if (this.neverAnalysisReviews) {
        this.clearNeverReadAnalysisReviews(this.$route.params.company_uuid)
      }
    },
    /**
     * データ種別の変更
     * @param { Object } value
     * @return { Void }
     */
    changeDataType(value) {
      if (this.loading) return
      this.$router.push({ query: { ...this.$route.query, type: value.id } })
      switch (value.id) {
      case 'scores':
        actionEvent('RevAnalReputation')
        break
      case 'counts':
        actionEvent('RevAnalPostNum')
        break
      case 'totalCounts':
        actionEvent('RevAnalPostCum')
        break
      default:
        break
      }
    },
    /**
     * 日, 週, 月の変更
     * @param { Object } value
     * @return { Void }
     */
    changeDate(value) {
      if (this.loading) return
      this.setDate(value)
      this.fetchData(false)
      switch (value.id) {
      case 'day':
        actionEvent('RevAnalDay')
        break
      case 'week':
        actionEvent('RevAnalWeek')
        break
      case 'month':
        actionEvent('RevAnalMonth')
        break
      default:
        break
      }
    },
    /**
     * 期間の変更
     * @param { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      if (this.loading) return
      this.setPeriod(value)
      this.fetchData(true)
    },
    /**
     * グラフ表示対象の変更
     * @param  { String } value
     * @return { Void }
     */
    changeDisplayGraphs(value) {
      if (this.loading) return
      this.setDisplayGraphs(value)
      this.fetchData(false)
    },
    /**
     * 分析軸の変更
     * @param  { Object } value
     * @return { Void }
     */
    async changePageType(value) {
      if (this.loading) return
      this.$router.push({ query: { ...this.$route.query, fields: value.fields } })
    },
    /**
     * クロス分析の変更
     * @param  { String } value
     * @return { Void }
     */
    changeCrossSearchField(value) {
      if (this.loading) return
      this.setCrossSearchField(value)
      this.fetchData(true)
    },
    /**
     * テーブル表示リストの変更
     * @param  { String } value
     * @return { Void }
     */
    changeIndividualAnalysisField(value) {
      if (this.loading) return
      this.setIndividualAnalysisField(value)
      this.fetchData(false)
    },
    /**
     * 競合の表示有無を切り替え
     * @param  { Boolean } isCompetitor
     * @return { Void }
     */
    async changeCompetitorMode(isCompetitor) {
      if (this.loading) return
      if (isCompetitor) {
        this.$router.push({ query: { ...this.$route.query, mode: 'competitor' } })
      } else {
        this.$router.push({ query: {  ...this.$route.query, mode: undefined  } })
      }
    },
    /**
     * @return { Void }
     */
    async setExportCsv() {
      const isSuccess = await this.exportCsv(this.requestParams)
      if (!isSuccess) return
      this.$router.push({ name: 'CompaniesExportResults' })
      actionEvent('CsvDownload')
    }
  }
}
</script>
